'use client';

import { Cross } from '@cat-home-experts/mortar-iconography-web';
import { Button, Heading, Modal, Text } from '@checkatrade/web-ui';
import { useIsMobileScreen, useLocalStorage } from '@checkatrade/web-utils';
import { useEffect, useMemo, useState } from 'react';

import { track } from 'lib/sdks/analytics';
import { Experiment, useOptimizely } from 'lib/sdks/optimizely';

const BRANCH_APP_PROMOTION_POPUP_URL = 'https://9tuma.app.link/AbtuwoOUxRb';

export function AppPromotionPopup() {
  const isMobile = useIsMobileScreen();
  const [show, setShow] = useState(false);

  const [seen, setSeen] = useLocalStorage('global-popup-seen', false);
  const { decide, loading } = useOptimizely();

  const decision = useMemo(() => {
    if (loading) {
      return 'off';
    }

    if (!isMobile) {
      return 'off';
    }

    return decide(Experiment.APP_PROMOTION_POPUP_SEARCH)?.variationKey ?? 'off';
  }, [decide, isMobile, loading]);

  useEffect(() => {
    track('app_promotion_popup.ready');
  }, []);

  useEffect(() => {
    if (decision !== 'on' || seen || !isMobile) return;

    function handle(event: Event) {
      event.preventDefault();
      event.stopPropagation();
      track('app_promotion_popup.show');
      setShow(true);
      setSeen(true);

      document.removeEventListener('mousedown', handle, false);
      document.removeEventListener('touchstart', handle, false);
      document.removeEventListener('mousemove', handle, false);
      document.removeEventListener('scroll', handle, false);
    }

    document.addEventListener('mousedown', handle, false);
    document.addEventListener('touchstart', handle, false);
    document.addEventListener('mousemove', handle, false);
    document.addEventListener('scroll', handle, false);

    return () => {
      document.removeEventListener('mousedown', handle, false);
      document.removeEventListener('touchstart', handle, false);
      document.removeEventListener('mousemove', handle, false);
      document.removeEventListener('scroll', handle, false);
    };
  }, [decision, isMobile, seen, setSeen]);

  if (decision === 'off' || !show) {
    return null;
  }

  function trackAppDownload() {
    track('global.app_download_click');
    setShow(false);
  }

  function close() {
    track('app_promotion_popup.close');
    setShow(false);
  }

  return (
    <Modal
      label="Download the Checkatrade app"
      onClose={close}
      className={{
        dialog: 'rounded-xl border-none',
        popover:
          'flex min-h-fit flex-col items-center justify-center gap-4 px-6 pb-6 pt-2',
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        width={260}
        height={160}
        src="https://images.ctfassets.net/5kq8dse7hipf/3zcy1CNDAITvANEhHNs2Wr/845798c764afa18c163cb096bf513edf/original.webp?w=520&h=320&q=90&fm=webp"
        alt="Download the Checkatrade app banner"
      />
      <Heading variant="titleMD" as="h2" weight="bold" className="text-center">
        Start your search with ease!
      </Heading>
      <Text className="max-w-96 text-center">
        Search, book, and chat with trusted tradespeople in the Checkatrade app
      </Text>
      <Button
        href={BRANCH_APP_PROMOTION_POPUP_URL}
        onClick={trackAppDownload}
        target="_blank"
      >
        Download now
      </Button>
      <Button
        className="absolute right-1 top-1"
        variant="unstyled"
        iconOnly
        onClick={close}
        aria-label="Close"
      >
        <Cross size={32} color="#adadad" />
      </Button>
    </Modal>
  );
}
